import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import { env } from '@/lib/env';
import { formatSessionDay, isNoSectionMeetingType } from '@/utils/meetingType';

// 「中」= システム自動処理中 / それ以外 = 人間対応 or 終端
// `not_completed` / `feedback` は backend に渡さず frontend で絞る統合フィルタ
const STATUS_OPTIONS = [
  { value: 'not_completed', label: '完了以外すべて' },
  { value: '', label: 'すべてのステータス' },
  { value: 'transcribing', label: '文字起こし中' },
  { value: 'transcribing_failed', label: '文字起こし失敗' },
  { value: 'section_config_pending', label: 'セクション設定中' },
  { value: 'ai_evaluating', label: env.VITE_SHOW_AI_FEEDBACK ? 'AI評価中' : 'サマリ作成中' },
  { value: 'ai_evaluation_failed', label: env.VITE_SHOW_AI_FEEDBACK ? 'AI 評価失敗' : 'サマリ作成失敗' },
  { value: 'feedback', label: 'フィードバック中' },
  { value: 'overall_feedback_pending', label: '全体分析中' },
  { value: 'overall_analysis_approval_pending', label: '全体分析承認待ち' },
  { value: 'coach_ack_pending', label: 'コーチ確認中' },
  { value: 'completed', label: '完了' },
];

const DEFAULT_STATUS_FILTER = 'not_completed';

// セッション一覧フィルタの永続化。
// 久保田さん要望: ページ離脱で毎回デフォルト (完了以外全て) に戻るのが手間。
// 同じブラウザで継続的に作業する想定なので localStorage 1 key に集約する
// (端末をまたぐ同期は別途 user.preferences 化が必要)。
const FILTER_STORAGE_KEY = 'delphi.sessionList.filters';

const ALLOWED_STATUS_FILTERS = new Set<string>([
  '',
  'not_completed',
  'transcribing',
  'transcribing_failed',
  'section_config_pending',
  'ai_evaluating',
  'ai_evaluation_failed',
  'feedback',
  'overall_feedback_pending',
  'overall_analysis_approval_pending',
  'coach_ack_pending',
  'completed',
]);

interface StoredFilters {
  statusFilter: string;
  myOnly: boolean;
  unreadAnalysisOnly: boolean;
}

function loadStoredFilters(): Partial<StoredFilters> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Partial<StoredFilters>;
  } catch {
    return {};
  }
}

function saveStoredFilters(filters: StoredFilters): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // QuotaExceeded / privacy mode などは無視 (機能継続を優先)
  }
}

interface Session {
  id: string; client_name: string; coach_name: string; coach_user_id: string;
  day_type: string; serial_no: number; status: SessionStatus;
  day1_variant?: 'first' | 'repeat' | null;
  outcome: string | null; session_date: string | null; updated_at: string;
  status_detail: 'uploading' | 'upload_failed' | 'start_pending' | null;
  requires_my_action: boolean;
  pending_reviewer_names?: string[];
  overall_analysis_ready?: boolean;
  overall_analysis_unread?: boolean | null;
  purged?: boolean;
}

function sessionStatusLabel(session: Session) {
  if (
    (session.status !== 'feedback_pending' && session.status !== 'feedback_in_progress') ||
    !session.pending_reviewer_names?.length
  ) {
    return undefined;
  }
  return `フィードバック中(${session.pending_reviewer_names.join('、')})`;
}

function statusLabel(s: Session) {
  if (s.purged) {
    return '削除済み';
  }
  if (s.status_detail === 'uploading') {
    return '音声アップロード中';
  }
  if (s.status_detail === 'upload_failed') {
    return '音声アップロード失敗';
  }
  if (s.status_detail === 'start_pending') {
    return '処理開始待ち';
  }
  return undefined;
}

function statusClass(s: Session) {
  if (s.purged) return 'bg-gray-200 text-gray-600';
  if (s.status_detail === 'uploading') return 'bg-blue-100 text-blue-800';
  if (s.status_detail === 'upload_failed') return 'bg-red-100 text-red-800';
  if (s.status_detail === 'start_pending') return 'bg-yellow-100 text-yellow-800';
  return undefined;
}

export function SessionListPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.currentUser);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    const stored = loadStoredFilters();
    if (stored.unreadAnalysisOnly === true) return '';
    // 空文字 '' は「すべてのステータス」として有効な選択値なので truthy check ではなく
    // 「string でかつ ALLOWED に居る」で判定する必要がある (久保田さん指摘の hotfix)
    return typeof stored.statusFilter === 'string' && ALLOWED_STATUS_FILTERS.has(stored.statusFilter)
      ? stored.statusFilter
      : DEFAULT_STATUS_FILTER;
  });
  const [myOnly, setMyOnly] = useState(() => {
    const stored = loadStoredFilters();
    if (stored.unreadAnalysisOnly === true) return false;
    return typeof stored.myOnly === 'boolean' ? stored.myOnly : false;
  });
  const [unreadAnalysisOnly, setUnreadAnalysisOnly] = useState(() => {
    const stored = loadStoredFilters();
    return typeof stored.unreadAnalysisOnly === 'boolean' ? stored.unreadAnalysisOnly : false;
  });
  const isFeedbacker = user?.is_feedbacker && !user?.is_admin;

  // フィルタ変更を localStorage に保存し、次回訪問時に復元できるようにする
  useEffect(() => {
    saveStoredFilters({ statusFilter, myOnly, unreadAnalysisOnly });
  }, [statusFilter, myOnly, unreadAnalysisOnly]);

  const fetchSessions = useCallback(() => {
    setLoading(true);
    // 未読件数には完了済みを含む全ステータスが必要なため、一覧は全件取得して
    // status / 担当 / 未読を frontend で組み合わせる。
    api.get('/sessions', { params: {} })
      .then(r => {
        setSessions(r.data as Session[]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRowClick = (s: Session) => {
    // session.id == pipeline_job.id (backend で submit 時に揃えている)。
    // 進行中ステータスは jobId を URL に明示して pipeline polling / SectionConfig を成立させる。
    if (s.status === 'feedback_pending' || s.status === 'feedback_in_progress') {
      // 担当コーチ本人でも reviewer 割当がある場合は /review で判定を優先する。
      navigate(
        user?.is_feedbacker && s.requires_my_action
          ? `/sessions/${s.id}/review${s.overall_analysis_unread ? '?tab=analysis' : ''}`
          : `/sessions/${s.id}/feedback${s.overall_analysis_unread ? '?tab=analysis' : ''}`,
      );
    } else if (
      s.status === 'overall_feedback_pending' ||
      s.status === 'overall_analysis_approval_pending' ||
      s.status === 'coach_ack_pending' ||
      s.status === 'completed'
    ) {
      navigate(`/sessions/${s.id}/feedback${s.overall_analysis_unread ? '?tab=analysis' : ''}`);
    } else if (s.status === 'section_config_pending') {
      if (isNoSectionMeetingType(s.day_type)) {
        navigate(`/sessions/${s.id}/status?jobId=${s.id}`);
        return;
      }
      // Day 別の section レイアウトを表示するため day を URL に渡す
      navigate(`/sessions/${s.id}/sections?jobId=${s.id}&day=${encodeURIComponent(s.day_type)}`);
    } else if (s.status === 'transcribing_failed' || s.status === 'ai_evaluation_failed') {
      navigate(`/sessions/${s.id}/status?jobId=${s.id}`);
    } else {
      navigate(`/sessions/${s.id}/status?jobId=${s.id}`);
    }
  };
  const canOpenSession = (s: Session) => {
    const isCoachOwner = String(s.coach_user_id) === String(user?.id);
    const isVisibleFinalStatus =
      s.status === 'overall_feedback_pending' ||
      s.status === 'overall_analysis_approval_pending' ||
      s.status === 'coach_ack_pending' ||
      s.status === 'completed';
    const isFailedStatus = s.status === 'transcribing_failed' || s.status === 'ai_evaluation_failed';
    const isProcessingStatus =
      s.status === 'transcribing' ||
      s.status === 'section_config_pending' ||
      s.status === 'ai_evaluating';
    return Boolean(
      user?.is_admin ||
        s.requires_my_action ||
        (isProcessingStatus && isCoachOwner) ||
        (isFailedStatus && isCoachOwner) ||
        (isVisibleFinalStatus && (isCoachOwner || user?.is_feedbacker)),
    );
  };
  const canDeleteSession = (s: Session) => Boolean(user?.is_admin || String(s.coach_user_id) === String(user?.id));
  const handleDeleteSession = async (s: Session) => {
    if (!window.confirm(`${s.client_name} のセッションを削除します。よろしいですか？`)) return;
    setDeletingId(s.id);
    try {
      await api.delete(`/sessions/${s.id}`);
      fetchSessions();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">セッション一覧</h1>
        <div className="flex items-center gap-3">
          {isFeedbacker && (
            <button type="button" onClick={() => {
              setMyOnly(prev => {
                const next = !prev;
                if (next) setUnreadAnalysisOnly(false);
                return next;
              });
            }}
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                myOnly ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              {myOnly ? '担当分のみ表示中' : '担当分のみ'}
            </button>
          )}
          {isFeedbacker && (
            <button
              type="button"
              onClick={() => {
                setUnreadAnalysisOnly(prev => {
                  const next = !prev;
                  if (next) {
                    setStatusFilter('');
                    setMyOnly(false);
                  }
                  return next;
                });
              }}
              title="完成した全体分析をまだ開いていない担当セッションだけを表示"
              className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                unreadAnalysisOnly
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {unreadAnalysisOnly ? '全体分析未読のみ表示中' : '全体分析未読のみ'}（
              {sessions.filter(s => s.overall_analysis_unread === true).length}）
            </button>
          )}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm">
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><LoadingState /></div>
      ) : (() => {
        let displayed = statusFilter === 'not_completed'
          ? sessions.filter(s => s.status !== 'completed')
          : statusFilter === 'feedback'
            ? sessions.filter(s => s.status === 'feedback_pending' || s.status === 'feedback_in_progress')
            : statusFilter
              ? sessions.filter(s => s.status === statusFilter)
              : sessions;
        displayed = myOnly ? displayed.filter(s => s.requires_my_action) : displayed;
        if (isFeedbacker && unreadAnalysisOnly) {
          displayed = displayed.filter(s => s.overall_analysis_unread === true);
        }
        return (
        <>
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500 w-20">要対応</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">実施日</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">クライアント</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">コーチ</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Day</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">ステータス</th>
                {isFeedbacker && (
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">全体分析</th>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map(s => {
                const canOpen = canOpenSession(s);
                return (
                <tr
                  key={s.id}
                  onClick={canOpen ? () => handleRowClick(s) : undefined}
                  className={
                    s.requires_my_action
                      ? 'bg-red-50 font-semibold hover:bg-red-100 cursor-pointer'
                      : canOpen
                        ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
                        : 'cursor-default text-gray-400'
                  }
                >
                  <td className="px-3 py-3">
                    {s.requires_my_action && (
                      <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                        要対応
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{s.session_date ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.client_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{s.coach_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{formatSessionDay(s.day_type, s.day1_variant)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={s.status}
                      labelOverride={statusLabel(s) ?? sessionStatusLabel(s)}
                      classNameOverride={statusClass(s)}
                    />
                  </td>
                  {isFeedbacker && (
                    <td className="px-4 py-3">
                      {s.purged ? (
                        <span className="rounded bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-500">
                          削除済み
                        </span>
                      ) : typeof s.overall_analysis_unread === 'boolean' && s.overall_analysis_ready ? (
                        s.overall_analysis_unread ? (
                          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                            未読
                          </span>
                        ) : (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            既読
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    {canDeleteSession(s) && (
                      <button
                        type="button"
                        disabled={deletingId === s.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleDeleteSession(s);
                        }}
                        className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingId === s.id ? '削除中' : '削除'}
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {displayed.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-400">
              {isFeedbacker && unreadAnalysisOnly
                ? '全体分析が未読のセッションはありません'
                : '該当するセッションはありません'}
            </div>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-400">{displayed.length} 件表示</p>
        </>
        );
      })()}
    </div>
  );
}
